import { memo, useMemo, useRef } from 'react';
import { MarkdownRenderer } from './MarkdownRenderer';
import { closeIncompleteMarkdownTokens, settledMarkdownLength } from '../utils';

interface StreamingMessageProps {
    content: string;
    isExpanded: boolean;
    isDark?: boolean;
    primaryColor?: string;
    apiUrl?: string;
}

/**
 * Markdown that has finished streaming and can no longer change.
 *
 * memo() on the rendered output is the point of the split: without it, every
 * finished paragraph is re-parsed and re-diffed on every animation frame just
 * to produce byte-identical output. Because `content` here only ever grows at
 * block boundaries, this re-renders a few times per message rather than ~60
 * times per second.
 */
const SettledMarkdown = memo(function SettledMarkdown(props: StreamingMessageProps) {
    return (
        <MarkdownRenderer
            content={props.content}
            isExpanded={props.isExpanded}
            isDark={props.isDark}
            primaryColor={props.primaryColor}
            apiUrl={props.apiUrl}
        />
    );
});

export function StreamingMessage({ content, isExpanded, isDark = false, primaryColor, apiUrl }: StreamingMessageProps) {
    // High-water mark for the settled boundary.
    //
    // settledMarkdownLength() is conservative but not monotonic on its own: when
    // a list or code fence begins, the last safe boundary moves backwards. Left
    // unclamped that would pull already-painted text back into the live tail and
    // visibly re-render it. Clamping to the maximum seen keeps the prefix
    // append-only, which is what the memo above relies on.
    const settledRef = useRef(0);

    // A new message (or a cleared one) resets the mark; `content` for a given
    // turn only ever grows, so a shorter string means we are on a new turn.
    const prevLenRef = useRef(0);
    if (content.length < prevLenRef.current) {
        settledRef.current = 0;
    }
    prevLenRef.current = content.length;

    const next = settledMarkdownLength(content);
    if (next > settledRef.current) {
        settledRef.current = next;
    }
    const settledLen = Math.min(settledRef.current, content.length);

    const settled = content.slice(0, settledLen);
    const tail = content.slice(settledLen);

    // Only the tail needs its dangling `**`/backtick/link closed — the settled
    // half is complete by definition.
    const tailContent = useMemo(() => closeIncompleteMarkdownTokens(tail), [tail]);

    // Nothing settled yet (short answer, or an open code fence): render the
    // whole thing as one live block, exactly as before.
    if (!settled) {
        return (
            <MarkdownRenderer content={tailContent} isExpanded={isExpanded} isDark={isDark} primaryColor={primaryColor} apiUrl={apiUrl} />
        );
    }

    return (
        <>
            <SettledMarkdown content={settled} isExpanded={isExpanded} isDark={isDark} primaryColor={primaryColor} apiUrl={apiUrl} />
            {tail ? (
                <MarkdownRenderer content={tailContent} isExpanded={isExpanded} isDark={isDark} primaryColor={primaryColor} apiUrl={apiUrl} />
            ) : null}
        </>
    );
}
